import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ArrowUpDown, Download, ChevronRight, FileSpreadsheet, FileText, Code2, Loader2 } from 'lucide-react';
import { claudeQueryService, ExportRequestDTO } from '../../../../../services/claudeQueryService';
import { VizSwitcher, type VizType } from './VizSwitcher';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface ColumnDefinition {
    name: string;
    displayName: string;
    dataType: string;
    format?: string;
    width?: number;
    sortable: boolean;
    filterable: boolean;
}

interface TableBlockData {
    title?: string;
    description?: string;
    columns: ColumnDefinition[];
    rows: Record<string, any>[];
    totalRowCount: number;
    exportable: boolean;
    exportFormats: string[];
}

interface TableBlockProps {
    data: TableBlockData;
    generatedSQL?: string;
    /** Callback for viz type switching */
    onVizSwitch?: (type: VizType) => void;
}

type SortConfig = {
    column: string;
    direction: 'asc' | 'desc';
} | null;

type ExportFormat = 'csv' | 'xlsx' | 'json' | 'html';

const INITIAL_VISIBLE_ROWS = 10;

export const TableBlock: React.FC<TableBlockProps> = ({ data, generatedSQL, onVizSwitch }) => {
    const { t } = useTranslation();
    const [sortConfig, setSortConfig] = useState<SortConfig>(null);
    const [showAll, setShowAll] = useState(false);
    const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
    const [showExportMenu, setShowExportMenu] = useState(false);

    const sortedRows = useMemo(() => {
        if (!sortConfig) return data.rows;

        return [...data.rows].sort((a, b) => {
            const aVal = a[sortConfig.column];
            const bVal = b[sortConfig.column];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            return sortConfig.direction === 'asc'
                ? aStr < bStr ? -1 : aStr > bStr ? 1 : 0
                : aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
        });
    }, [data.rows, sortConfig]);

    const visibleRows = showAll ? sortedRows : sortedRows.slice(0, INITIAL_VISIBLE_ROWS);
    const hasMore = sortedRows.length > INITIAL_VISIBLE_ROWS;

    const handleSort = (column: ColumnDefinition) => {
        if (!column.sortable) return;
        setSortConfig(current => {
            if (!current || current.column !== column.name)
                return { column: column.name, direction: 'asc' };
            if (current.direction === 'asc')
                return { column: column.name, direction: 'desc' };
            return null;
        });
    };

    const formatValue = (value: any, column: ColumnDefinition): string => {
        if (value === null || value === undefined) return '-';

        if (column.dataType === 'number' && typeof value === 'number') {
            if (column.format) {
                const decimals = parseInt(column.format.replace('N', ''));
                return value.toFixed(decimals);
            }
            return value.toLocaleString('nb-NO');
        }

        if (column.dataType === 'date' && value) {
            try {
                const date = new Date(value);
                return date.toLocaleDateString('nb-NO');
            } catch {
                return String(value);
            }
        }

        return String(value);
    };

    const handleExport = async (format: ExportFormat) => {
        setExportingFormat(format);
        setShowExportMenu(false);

        try {
            const request: ExportRequestDTO = {
                format,
                title: data.title || t('manager.modules.claudeQuery.table.exportTitle', { '0': new Date().toISOString().slice(0, 10) }, `Compass Eksport ${new Date().toISOString().slice(0, 10)}`),
                columns: data.columns.map(col => ({
                    name: col.name,
                    displayName: col.displayName,
                    dataType: col.dataType,
                    format: col.format,
                })),
                rows: sortedRows,
                generatedSQL,
            };

            const exportResult = await claudeQueryService.exportData(request);

            // Last ned filen direkte
            const { blob, filename } = await claudeQueryService.downloadExportFile(exportResult.fileId);
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        } catch (error) {
            console.error('Export failed:', error);
            // Fallback til klient-side CSV
            if (format === 'csv') {
                handleClientSideCsvExport();
            }
        } finally {
            setExportingFormat(null);
        }
    };

    const handleClientSideCsvExport = () => {
        const headers = data.columns.map(col => col.displayName).join(';');
        const rows = sortedRows.map(row =>
            data.columns.map(col => {
                const formatted = formatValue(row[col.name], col);
                return formatted.includes(';') || formatted.includes('\n')
                    ? `"${formatted.replace(/"/g, '""')}"`
                    : formatted;
            }).join(';')
        );

        const csv = [headers, ...rows].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${data.title || 'data'}-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    if (data.columns.length === 0 || data.rows.length === 0) {
        return (
            <div className="text-sm text-gray-500 italic py-2">
                {t('manager.modules.claudeQuery.table.noDataToShow', {}, 'Ingen data å vise')}
            </div>
        );
    }

    const exportButtons = (
        <div className="relative">
            <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={!!exportingFormat}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded transition-colors border border-gray-200"
            >
                {exportingFormat ? (
                    <Loader2 size={12} className="animate-spin" />
                ) : (
                    <Download size={12} />
                )}
                {t('common.buttons.export', {}, 'Eksporter')}
            </button>

            {showExportMenu && (
                <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                    <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                        <button
                            onClick={() => handleExport('csv')}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                            <FileText size={14} className="text-green-600" />
                            CSV (.csv)
                        </button>
                        <button
                            onClick={() => handleExport('xlsx')}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                            <FileSpreadsheet size={14} className="text-green-700" />
                            Excel (.xlsx)
                        </button>
                        <button
                            onClick={() => handleExport('html')}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                            <Code2 size={14} className="text-blue-600" />
                            HTML (.html)
                        </button>
                        <button
                            onClick={() => handleExport('json')}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                            <Code2 size={14} className="text-orange-600" />
                            JSON (.json)
                        </button>
                    </div>
                </>
            )}
        </div>
    );

    return (
        <div className="mt-2">
            <div className="flex items-center justify-between mb-1 gap-2">
                {data.title ? (
                    <h4 className="text-sm font-semibold text-gray-700">{data.title}</h4>
                ) : (
                    <div />
                )}
                <div className="flex items-center gap-2">
                    {onVizSwitch && (
                        <VizSwitcher
                            currentType="table"
                            onSwitch={onVizSwitch}
                            sourceBlockType="table"
                        />
                    )}
                    {exportButtons}
                </div>
            </div>

            <div className="border rounded overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                            <tr>
                                {data.columns.map((column, idx) => (
                                    <th
                                        key={idx}
                                        onClick={() => handleSort(column)}
                                        className={`px-3 py-2 text-left text-xs font-semibold text-gray-600 border-b ${
                                            column.sortable ? 'cursor-pointer hover:bg-gray-200' : ''
                                        }`}
                                    >
                                        <div className="flex items-center gap-1">
                                            <span>{column.displayName}</span>
                                            {column.sortable && (
                                                <span className="text-gray-400">
                                                    {sortConfig?.column === column.name ? (
                                                        sortConfig.direction === 'asc'
                                                            ? <ChevronUp size={12} />
                                                            : <ChevronDown size={12} />
                                                    ) : (
                                                        <ArrowUpDown size={10} className="opacity-50" />
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row, rowIdx) => (
                                <tr key={rowIdx} className="border-b hover:bg-gray-50">
                                    {data.columns.map((column, colIdx) => (
                                        <td key={colIdx} className="px-3 py-1.5 text-xs text-gray-700">
                                            {formatValue(row[column.name], column)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex items-center justify-between mt-1.5 text-xs text-gray-500">
                <span>
                    {showAll
                        ? t('manager.modules.claudeQuery.table.rowCount', { '0': sortedRows.length }, `${sortedRows.length} rader`)
                        : t('manager.modules.claudeQuery.table.rowCountPartial', { '0': visibleRows.length, '1': sortedRows.length }, `${visibleRows.length} av ${sortedRows.length} rader`)
                    }
                </span>
                {hasMore && !showAll && (
                    <button
                        onClick={() => setShowAll(true)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                    >
                        {t('manager.modules.claudeQuery.table.showAll', {}, 'Vis alle')} <ChevronRight size={12} />
                    </button>
                )}
                {showAll && hasMore && (
                    <button
                        onClick={() => setShowAll(false)}
                        className="text-blue-600 hover:text-blue-800"
                    >
                        {t('manager.modules.claudeQuery.table.showLess', {}, 'Vis mindre')}
                    </button>
                )}
            </div>
        </div>
    );
};
